// Import / export the rules as a spreadsheet (prototype import dialog; the
// approved format — plan §14 Phase 3): one row per rule, a `set` column and
// one column per deciding column; an empty cell, `*` or `any` is Any; several
// values separated by commas; `not …` for "every value but these". The file
// is read by the server with the reader record import uses (CSV / XLSX);
// rows naming a value the master does not hold are skipped and listed. Import
// writes the DRAFT only.

import { useRef, useState } from 'react'
import { Download, Info, Upload } from 'lucide-react'
import { tw } from '../../testHooks'
import { KitModal } from '../../designerKit/KitModal'
import type { UdaImportResult } from '../types'
import { ANY, canonValue, isExcept } from '../rules'
import { driverLabel, formOf } from './model'
import { useSetup } from './state'
import { Banner, HintBox, Seg } from './ui'

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  const step = 0x8000
  for (let i = 0; i < bytes.length; i += step) bin += String.fromCharCode(...bytes.subarray(i, i + step))
  return btoa(bin)
}

function messageOf(err: unknown): string {
  const e = err as { message?: string; detail?: { message?: string; details?: { errors?: string[] } }; response?: { data?: { detail?: { message?: string } | string } } }
  const d = e?.detail || (typeof e?.response?.data?.detail === 'object' ? e.response.data.detail : undefined)
  const extra = (e?.detail?.details?.errors || []).join('; ')
  return [d?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : '') || e?.message || 'The import failed', extra].filter(Boolean).join(' — ')
}

export default function ImportExportDialog({ onClose }: { onClose: () => void }) {
  const { formNo, draft, transport, objName, notify, mode: setupMode } = useSetup()
  const form = formOf(draft.layer, formNo)
  const drivers = form?.drivers || []
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [mode, setMode] = useState<'replace' | 'append'>('replace')
  const [busy, setBusy] = useState<'import' | 'export' | null>(null)
  const [result, setResult] = useState<UdaImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const designOnly = setupMode === 'design'

  const doImport = async () => {
    if (!file) { setError('Choose a filled spreadsheet first.'); return }
    setBusy('import'); setError(null); setResult(null)
    try {
      if (!(await draft.flush())) { setError('Your last change was not saved. Fix or discard it before importing.'); return }
      const b64 = toBase64(await file.arrayBuffer())
      const r = await transport.importRules(objName, formNo, { fileBase64: b64, filename: file.name, mode, baseVersion: draft.version })
      setResult(r)
      await draft.reload()
      notify('success', `Read ${r.applied + r.skipped.length} rows: ${r.applied} applied${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}.`)
    } catch (err) {
      console.warn('[uda setup] import failed', err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }
  const doExport = async () => {
    setBusy('export'); setError(null)
    try {
      if (!(await draft.flush())) { setError('Your last change was not saved. Fix or discard it before exporting.'); return }
      const r = await transport.exportRules(objName, formNo)
      const blob = new Blob([r.csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = r.filename || `${objName}_rules.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      console.warn('[uda setup] export failed', err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }
  const cell = (v: unknown) => {
    const c = canonValue(v)
    if (c === ANY) return ''
    if (isExcept(c)) return `not ${c.any_except.slice(0, 3).join(', ')}${c.any_except.length > 3 ? ', …' : ''}`
    return `${(c as string[]).slice(0, 3).join(', ')}${(c as string[]).length > 3 ? ', …' : ''}`
  }
  return (
    <KitModal open onClose={onClose} title="Import or export the rules"
      footer={(
        <>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy || designOnly} onClick={() => { void doExport() }}
            {...tw('action', 'uda-export')}>
            {busy === 'export' ? <span className="uds-spin" /> : <Download size={14} />} Export current rules
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || !file || designOnly} onClick={() => { void doImport() }}
            {...tw('action', 'uda-import-run')}>
            {busy === 'import' ? <span className="uds-spin" /> : <Upload size={14} />} Import
          </button>
        </>
      )}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 12 }}>
        <div className="uds-hint" style={{ margin: 0 }}>
          For large mappings, keep them in a spreadsheet. One row per rule, one column per deciding column. Separate
          several values with commas, leave a cell empty for Any, and write <span className="uds-mono">not …</span> for
          every value but those.
        </div>
        {designOnly && <Banner tone="i">Importing writes an organization&apos;s draft; in the transaction designer, edit the rules on the screen.</Banner>}
        <div className="uds-tblwrap">
          <table className="uds-tbl">
            <thead><tr><th>set</th>{drivers.map(d => <th key={d.key}>{d.key}<div className="uds-thsub">{driverLabel(d)}</div></th>)}</tr></thead>
            <tbody>
              {(form?.sets || []).slice(0, 3).map(s => (s.rules || []).slice(0, 1).map((r, i) => (
                <tr key={`${s.key}${i}`}><td>{s.key}</td>{drivers.map(d => <td key={d.key} className="uds-mono">{cell(r[d.key])}</td>)}</tr>
              )))}
            </tbody>
          </table>
        </div>
        <HintBox icon={<Info size={15} />} style={{ margin: 0 }}>
          Codes or names are both accepted. Values not found in the master are reported and skipped. Nothing changes until
          you import, and the import itself is a draft change.
        </HintBox>
        <div>
          <div className="uds-lbl" style={{ marginTop: 0 }}>The rules in the file</div>
          <Seg value={mode} onChange={setMode} ariaLabel="Import mode"
            options={[{ value: 'replace', label: 'Replace the rules of the sets it names' }, { value: 'append', label: 'Add to the existing rules' }]} />
        </div>
        <label className="uds-lbl" htmlFor="uds-imp" style={{ marginTop: 0 }}>Upload a filled spreadsheet</label>
        <input id="uds-imp" ref={fileRef} className="uds-inp" type="file" accept=".xlsx,.xls,.csv"
          onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError(null) }} {...tw('field', 'uda-import-file')} />
        {error && <Banner tone="e">{error}</Banner>}
        {result && (
          <Banner tone={result.skipped.length ? 'w' : 'i'}>
            <b>{result.applied} rule{result.applied === 1 ? '' : 's'} imported{result.skipped.length ? `, ${result.skipped.length} row${result.skipped.length === 1 ? '' : 's'} skipped` : ''}.</b>
            {!!result.skipped.length && <ul>{result.skipped.map(s => <li key={s.row}>Row {s.row}: {s.reason}</li>)}</ul>}
            {!!result.problems?.length && <ul>{result.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>}
          </Banner>
        )}
      </div>
    </KitModal>
  )
}
